// Seed data for the in-browser mock backend. Only src/api/mock/* imports this file (plus the QA
// module, which re-exports TENANTS/ROLES/BREAKPOINTS for its audit screens). In production
// these records live in the Python backend's database.

export const TENANTS = [
  { id: "tenant-acme", name: "Acme Retail", plan: "Business", region: "ap-south-1" },
  { id: "tenant-globex", name: "Globex Logistics", plan: "Enterprise", region: "ap-south-2" },
  // Initech intentionally has no data so the tenant-scoped EMPTY state is reachable.
  { id: "tenant-initech", name: "Initech Support", plan: "Starter", region: "ap-southeast-1" }
];

// Role ids are what the API authorises against; "team roles" (Owner/Admin/Editor/Viewer) are the
// labels shown on the Team screen and map onto these ids (see TEAM_ROLE_TO_ROLE).
export const ROLES = [
  { id: "admin", label: "Admin" },
  { id: "agent", label: "Agent" },
  { id: "viewer", label: "Viewer" }
];

export const TEAM_ROLES = ["Owner", "Admin", "Editor", "Viewer"];
export const TEAM_ROLE_TO_ROLE = { Owner: "admin", Admin: "admin", Editor: "agent", Viewer: "viewer" };

export const ALL_PERMISSIONS = [
  "conversations:read", "conversations:write", "analytics:read", "billing:read", "users:manage",
  "business:manage", "bots:manage", "knowledge:manage", "leads:manage", "marketing:manage"
];

export const ROLE_PERMISSIONS = {
  admin: ["conversations:read", "conversations:write", "analytics:read", "billing:read", "users:manage", "business:manage", "bots:manage", "knowledge:manage", "leads:manage", "marketing:manage"],
  agent: ["conversations:read", "conversations:write", "analytics:read", "bots:manage", "knowledge:manage", "leads:manage", "marketing:manage"],
  viewer: ["conversations:read"]
};

// ---------------------------------------------------------------------------------------------
// Accounts. Every account belongs to exactly ONE tenant with ONE role, fixed when the account is
// created (self-registration creates a new tenant + Owner; an invite code joins an existing one).
// A real backend never exposes passwords like this — it exists because the mock has no server.
// ---------------------------------------------------------------------------------------------
const pw = "password123";
export const SEED_USERS = [
  { id: "u-acme-1", tenantId: "tenant-acme", name: "Aarav Sharma", email: "admin@chatbizai.com", password: pw, teamRole: "Owner", status: "Active", verified: true, lastActive: "Just now" },
  { id: "u-acme-2", tenantId: "tenant-acme", name: "Priya Reddy", email: "priya@chatbizz.com", password: pw, teamRole: "Admin", status: "Active", verified: true, lastActive: "12 min ago" },
  { id: "u-acme-3", tenantId: "tenant-acme", name: "Chandu", email: "chandu@chatbizz.com", password: pw, teamRole: "Editor", status: "Active", verified: true, lastActive: "1 hr ago" },
  { id: "u-acme-4", tenantId: "tenant-acme", name: "Sruthika", email: "sruthika@chatbizz.com", password: pw, teamRole: "Editor", status: "Active", verified: true, lastActive: "Today" },
  { id: "u-acme-5", tenantId: "tenant-acme", name: "rahul", email: "rahul@chatbizz.com", password: null, teamRole: "Editor", status: "Invited", verified: false, inviteCode: "482913", lastActive: "—" },
  { id: "u-acme-6", tenantId: "tenant-acme", name: "Meera Nair", email: "meera@chatbizz.com", password: pw, teamRole: "Viewer", status: "Active", verified: true, lastActive: "Yesterday" },
  { id: "u-globex-1", tenantId: "tenant-globex", name: "Rohan Mehta", email: "rohan@globex.test", password: pw, teamRole: "Owner", status: "Active", verified: true, lastActive: "Today" },
  { id: "u-globex-2", tenantId: "tenant-globex", name: "Meera Iyer", email: "meera@globex.test", password: pw, teamRole: "Editor", status: "Active", verified: true, lastActive: "Yesterday" },
  // Initech has an Owner but no other data, so every tenant-scoped EMPTY state is reachable via a real login.
  { id: "u-initech-1", tenantId: "tenant-initech", name: "Dinesh Rao", email: "admin@initech.test", password: pw, teamRole: "Owner", status: "Active", verified: true, lastActive: "Today" }
];

export const SEED_PROFILES = {
  "tenant-acme": {
    businessName: "Acme Retail", industry: "Retail", website: "https://acme.com", phone: "9876543210",
    address: "12 MG Road", city: "Hyderabad", state: "Telangana", country: "India",
    description: "Omni-channel retail brand.", domain: "acme.com", timezone: "Asia/Kolkata", language: "English"
  },
  "tenant-globex": {
    businessName: "Globex Logistics", industry: "Logistics", website: "https://globex.com", phone: "9000011122",
    address: "4 Port Road", city: "Chennai", state: "Tamil Nadu", country: "India",
    description: "Freight and last-mile delivery.", domain: "globex.com", timezone: "Asia/Kolkata", language: "English"
  },
  "tenant-initech": {
    businessName: "Initech Support", industry: "", website: "", phone: "", address: "", city: "", state: "", country: "",
    description: "", domain: "", timezone: "", language: ""
  }
};

// ---------------------------------------------------------------------------------------------
// Bots. `config` = Bot Configuration fields, `appearance` = Appearance & Branding fields. They
// live on the bot record, so Bot Management, Bot Configuration, Appearance and the visitor widget
// all read/write the same bot by id (GET/PATCH /api/bots/:id).
// ---------------------------------------------------------------------------------------------
export const DEFAULT_BOT_CONFIG = {
  tone: "friendly", primaryLanguage: "en", supportedLanguages: ["en"], instructions: "",
  fallbackMessage: "I’m sorry, I couldn’t find a reliable answer. Would you like me to connect you with a person?",
  prohibitedTopics: [], prohibitedTopicAction: "fallback", responseBoundaryEnabled: true,
  responseBoundaryScope: "", responseBoundaryMessage: "", responseBoundaryAction: "redirect",
  responseStyle: "conversational", responseLength: "balanced", responseFormat: "paragraphs", responseEmoji: false,
  customProhibitedTopics: [],
  leadCapture: { phone: "optional", company: false, message: false },
  handoff: {
    enabled: true, triggers: { explicitRequest: true, lowConfidence: false, keywordMatch: false },
    sensitivity: "", keywords: [], message: "", offHoursMode: "queue", offlineMessage: ""
  }
};

export const DEFAULT_APPEARANCE = {
  launcher: "Round Blue", position: "Bottom right", launcherSize: 58,
  botName: "ChatBizz AI", description: "Advanced AI Support Chat Bot",
  primary: "#007BFF", secondary: "#00D4FF", font: "Poppins",
  avatar: "", logo: "", headerText: "ChatBizz AI", headerStatus: "Online",
  welcome: "Hi! I'm ChatBizz AI 👋\nHow can I help you today?",
  showName: true, widgetPosition: "bottom-right", widgetWidth: 380, widgetHeight: 620,
  desktopOffsetX: 24, desktopOffsetY: 24, mobileWidth: 340, mobileHeight: 560
};

export const makeBot = (overrides = {}) => ({
  id: "", tenantId: "", name: "ChatBizz Assistant", description: "Answers customer questions on your website.",
  model: "GPT-4o", greeting: "Hi! How can I help you today?", status: "inactive", version: 1,
  ownerRole: "business_admin", createdAt: "2026-09-20T09:00:00Z", updatedAt: "2026-09-20T09:00:00Z",
  config: JSON.parse(JSON.stringify(DEFAULT_BOT_CONFIG)), appearance: { ...DEFAULT_APPEARANCE }, ...overrides
});

export const SEED_BOTS = [
  makeBot({ id: "bot-acme-1", tenantId: "tenant-acme", name: "Website Support Bot", description: "Handles pricing, demo and support questions on acme.com.", status: "active", version: 3, greeting: "Hi! Welcome to Acme Retail 👋 How can I help you today?" }),
  makeBot({ id: "bot-acme-2", tenantId: "tenant-acme", name: "Sales Assistant", description: "Qualifies sales leads.", status: "inactive", version: 1 }),
  makeBot({ id: "bot-globex-1", tenantId: "tenant-globex", name: "Logistics Assistant", description: "Tracks shipments and answers delivery questions.", status: "active", version: 2, greeting: "Hello! Ask me about shipments and deliveries." }),
  makeBot({ id: "bot-initech-1", tenantId: "tenant-initech", name: "Initech Help Bot", description: "Answers first-line support questions.", model: "GPT-4o mini", status: "active", version: 1, greeting: "Hi! Welcome to Initech Support 👋 How can I help?" })
];

// ---------------------------------------------------------------------------------------------
// Conversations (shared by the QA list screens, the visitor widget and the Agent Workspace).
// ---------------------------------------------------------------------------------------------
// Demo conversations are dated relative to when the data is first seeded, so a fresh demo always looks live.
const ago = (minutes) => new Date(Date.now() - minutes * 60000).toISOString();
const msg = (id, from, text, at) => ({ id, from, text, createdAt: at });
export const SEED_CONVERSATIONS = [
  {
    id: "c-acme-1", tenantId: "tenant-acme", botId: "bot-acme-1", customer: "Ramesh", channel: "WhatsApp", status: "Open", unread: 3,
    updatedAt: ago(6), createdAt: ago(8), priority: "high", assignedAgentId: null, visitorEmail: "ramesh@gmail.com", leadId: "L-101",
    messages: [
      msg("m-1", "visitor", "Hi, I can't log in to my account.", ago(8)),
      msg("m-2", "visitor", "How can I reset my password?", ago(7)),
      msg("m-3", "system", "Escalated — visitor requested a human agent", ago(6))
    ],
    tags: [{ id: "t-1", name: "password-reset" }, { id: "t-2", name: "login-issue" }], notes: [], fallbackStreak: 0, seed: true
  },
  {
    id: "c-acme-2", tenantId: "tenant-acme", botId: "bot-acme-1", customer: "Priya", channel: "Web chat", status: "Resolved", unread: 0,
    updatedAt: ago(35), createdAt: ago(45), priority: "medium", assignedAgentId: "u-acme-2", visitorEmail: "",
    messages: [
      msg("m-4", "visitor", "I need pricing details for the Growth plan.", ago(45)),
      msg("m-5", "agent", "The Growth plan is ₹9,999/month. Want a quote?", ago(37))
    ],
    tags: [], notes: [], fallbackStreak: 0, seed: true
  },
  {
    id: "c-acme-3", tenantId: "tenant-acme", botId: "bot-acme-1", customer: "Kumar", channel: "Email", status: "Pending", unread: 1,
    updatedAt: ago(1), createdAt: ago(3), priority: "low", assignedAgentId: "u-acme-3", visitorEmail: "",
    messages: [
      msg("m-6", "visitor", "Can I get a demo of ChatBiz AI?", ago(3)),
      msg("m-7", "system", "Assigned to Chandu", ago(2))
    ],
    tags: [], notes: [], fallbackStreak: 0, seed: true
  },
  {
    id: "c-acme-4", tenantId: "tenant-acme", botId: "bot-acme-1", customer: "Sneha", channel: "WhatsApp", status: "Open", unread: 2,
    updatedAt: ago(28), createdAt: ago(30), priority: "medium", assignedAgentId: null, visitorEmail: "",
    messages: [msg("m-8", "visitor", "Do you deliver to Hyderabad?", ago(30))],
    tags: [], notes: [], fallbackStreak: 0, seed: true
  },
  {
    id: "c-globex-1", tenantId: "tenant-globex", botId: "bot-globex-1", customer: "Arjun", channel: "Web chat", status: "Open", unread: 5,
    updatedAt: ago(10), createdAt: ago(20), priority: "medium", assignedAgentId: null, visitorEmail: "",
    messages: [msg("m-9", "visitor", "Where is my shipment GX-4412?", ago(20))],
    tags: [], notes: [], fallbackStreak: 0, seed: true
  },
  {
    id: "c-globex-2", tenantId: "tenant-globex", botId: "bot-globex-1", customer: "Meera", channel: "Email", status: "Resolved", unread: 0,
    updatedAt: ago(100), createdAt: ago(120), priority: "low", assignedAgentId: "u-globex-2", visitorEmail: "",
    messages: [msg("m-10", "visitor", "Please confirm delivery slot.", ago(120))],
    tags: [], notes: [], fallbackStreak: 0, seed: true
  },
  {
    id: "c-globex-3", tenantId: "tenant-globex", botId: "bot-globex-1", customer: "Vikram", channel: "WhatsApp", status: "Pending", unread: 1,
    updatedAt: ago(50), createdAt: ago(60), priority: "low", assignedAgentId: null, visitorEmail: "",
    messages: [msg("m-11", "visitor", "Need a quote for bulk freight.", ago(60))],
    tags: [], notes: [], fallbackStreak: 0, seed: true
  }
];

// ---------------------------------------------------------------------------------------------
// Leads, knowledge, invoices, analytics baselines
// ---------------------------------------------------------------------------------------------
export const LEAD_STATUSES = ["New", "Contacted", "Qualified", "Won", "Lost"];

const leadSeed = (id, tenantId, name, email, phone, source, intent, status, score, createdAt) => ({
  id, tenantId, name, email, phone, source, intent, status, score, createdAt, seed: true,
  timeline: [
    { at: createdAt, event: `Lead created via ${source}` },
    { at: createdAt, event: `Intent detected: ${intent}` },
    { at: createdAt, event: `Score calculated: ${score}` }
  ]
});

export const SEED_LEADS = [
  leadSeed("L-101", "tenant-acme", "Ramesh Kumar", "ramesh@gmail.com", "+91 98765 43210", "Website", "Pricing enquiry", "New", 82, "2026-09-11T10:24:00"),
  leadSeed("L-102", "tenant-acme", "Priya Sharma", "priya@acme.com", "+91 91234 56780", "WhatsApp", "Book a demo", "Qualified", 91, "2026-09-11T10:18:00"),
  leadSeed("L-103", "tenant-acme", "Kumar Rao", "kumar@zeta.io", "+91 99887 76655", "Facebook", "Support", "Contacted", 46, "2026-09-10T17:05:00"),
  leadSeed("L-104", "tenant-acme", "Sneha Reddy", "sneha@corp.in", "+91 90000 11122", "Email", "Integration query", "Lost", 22, "2026-09-10T09:50:00"),
  leadSeed("L-201", "tenant-globex", "Arjun Mehta", "arjun@fastmove.in", "+91 98100 22334", "Website", "Bulk freight quote", "New", 74, "2026-09-12T11:00:00")
];

export const SEED_KNOWLEDGE = [
  { id: "kb-1", tenantId: "tenant-acme", title: "Product FAQs", type: "FAQ", category: "Product", source: "Document", status: "Published", updatedAt: "2026-10-02T09:00:00Z", views: 284, question: "What does Acme sell?", answer: "Acme sells home and lifestyle products online and in stores.", seed: true },
  { id: "kb-2", tenantId: "tenant-acme", title: "Pricing & Policy Guide", type: "Article", category: "Pricing", source: "Document", status: "Published", updatedAt: "2026-10-01T09:00:00Z", views: 192, question: "", answer: "", seed: true },
  { id: "kb-3", tenantId: "tenant-acme", title: "Installation Guide", type: "Article", category: "Getting Started", source: "Document", status: "Draft", updatedAt: "2026-09-30T09:00:00Z", views: 74, question: "", answer: "", seed: true },
  { id: "kb-4", tenantId: "tenant-acme", title: "Troubleshooting", type: "Article", category: "Support", source: "URL", status: "Published", updatedAt: "2026-09-29T09:00:00Z", views: 158, question: "", answer: "", seed: true },
  { id: "kb-5", tenantId: "tenant-acme", title: "Company Overview", type: "Article", category: "Company", source: "Document", status: "Archived", updatedAt: "2026-09-28T09:00:00Z", views: 42, question: "", answer: "", seed: true },
  { id: "kb-6", tenantId: "tenant-globex", title: "Shipping Rates", type: "FAQ", category: "Pricing", source: "Document", status: "Published", updatedAt: "2026-09-27T09:00:00Z", views: 61, question: "How are rates calculated?", answer: "By weight, distance and service level.", seed: true }
];

export const INVOICES = [
  { id: "inv-1001", tenantId: "tenant-acme", period: "Aug 2026", amount: 12400, currency: "INR", status: "Paid" },
  { id: "inv-1002", tenantId: "tenant-acme", period: "Sep 2026", amount: 12400, currency: "INR", status: "Due" },
  { id: "inv-2001", tenantId: "tenant-globex", period: "Sep 2026", amount: 48900, currency: "INR", status: "Paid" }
];

// Historical baselines: activity that happened before this browser's data store existed. Live
// activity (visitor chats, leads, escalations created at runtime) is added on top of these, so a
// brand-new tenant starts at zero and every number moves as the product is actually used.
export const ANALYTICS = {
  "tenant-acme": {
    openConversations: 2, resolvedToday: 1, avgFirstResponseSec: 84,
    engagement: { totalMessages: 1250, totalSessions: 184, uniqueVisitors: 960 },
    widgetOpens: 3480, conversationsStarted: 925, messagesPerSession: 6.8,
    returningVisitorsRate: 38, aiResolutionRate: 92, escalationRate: 8,
    averageResponseTime: 18, unresolvedQuestions: 42,
    cta: { impressions: 2140, clicks: 386, clickThroughRate: 18 },
    leadConversion: { capturedLeads: 148, convertedLeads: 34, conversionRate: 23 }
  },
  "tenant-globex": {
    openConversations: 1, resolvedToday: 1, avgFirstResponseSec: 132,
    engagement: { totalMessages: 840, totalSessions: 121, uniqueVisitors: 610 },
    widgetOpens: 2190, conversationsStarted: 604, messagesPerSession: 7.0,
    returningVisitorsRate: 31, aiResolutionRate: 88, escalationRate: 12,
    averageResponseTime: 24, unresolvedQuestions: 57,
    cta: { impressions: 1520, clicks: 244, clickThroughRate: 16 },
    leadConversion: { capturedLeads: 96, convertedLeads: 19, conversionRate: 20 }
  },
  "tenant-initech": {
    openConversations: 0, resolvedToday: 0, avgFirstResponseSec: 0,
    engagement: { totalMessages: 0, totalSessions: 0, uniqueVisitors: 0 },
    widgetOpens: 0, conversationsStarted: 0, messagesPerSession: 0,
    returningVisitorsRate: 0, aiResolutionRate: 0, escalationRate: 0,
    averageResponseTime: 0, unresolvedQuestions: 0,
    cta: { impressions: 0, clicks: 0, clickThroughRate: 0 },
    leadConversion: { capturedLeads: 0, convertedLeads: 0, conversionRate: 0 }
  }
};

export const EMPTY_ANALYTICS = ANALYTICS["tenant-initech"];

// Responsive contract: what the layout MUST do at each Bootstrap 5 breakpoint.
export const BREAKPOINTS = [
  { key: "xs", label: "Extra small", min: 0, testWidth: 360, sidebar: "drawer", statColumns: 1 },
  { key: "sm", label: "Small", min: 576, testWidth: 576, sidebar: "drawer", statColumns: 2 },
  { key: "md", label: "Medium", min: 768, testWidth: 768, sidebar: "rail", statColumns: 2 },
  { key: "lg", label: "Large", min: 992, testWidth: 992, sidebar: "full", statColumns: 4 },
  { key: "xl", label: "Extra large", min: 1200, testWidth: 1200, sidebar: "full", statColumns: 4 },
  { key: "xxl", label: "XX-large", min: 1400, testWidth: 1400, sidebar: "full", statColumns: 4 }
];
